/**
 * SSE streaming client for POST /api/llm/messages
 */

import { FetchUtils } from '../utils/fetch-utils.js';
import { Logger } from '../utils/logger.js';
import { buildMessageRequest } from '../../components/ai-assistant/list-selection.js';
import {
  applySseEvent,
  createStreamState,
  snapshotStreamState,
  sseEventPayload,
  streamIncompleteError,
} from './sse-utils.js';

async function httpErrorPayload(res) {
  const isServerError = res.status >= 500 && res.status < 600;
  let backendMessage;
  if (!isServerError) {
    try {
      const body = await res.clone().json();
      if (typeof body?.message === 'string') backendMessage = body.message;
    } catch {
      // Non-JSON body (e.g. proxy error page)
    }
  }
  return {
    code: `http_${res.status}`,
    message: isServerError
      ? 'AI Assistant is temporarily unavailable. Please try again.'
      : backendMessage || `Request failed (HTTP ${res.status})`,
    retriable: isServerError,
  };
}

function throwStreamError(errPayload, onError) {
  onError?.(errPayload);
  const tagged = new Error(errPayload.message);
  tagged.streamError = errPayload;
  throw tagged;
}

export async function streamMessage({
  message,
  conversationId,
  listId,
  focus,
  action,
  proposalId,
  onUpdate,
  onError,
  signal,
}) {
  const body = JSON.stringify(
    buildMessageRequest({ message, conversationId, listId, focus, action, proposalId })
  );

  const res = await FetchUtils.fetchRaw('/api/llm/messages', {
    method: 'POST',
    body,
    signal,
  });

  if (!res.ok) {
    throwStreamError(await httpErrorPayload(res), onError);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const state = createStreamState();
  let result;

  while (true) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

      const jsonStr = sseEventPayload(line);
      if (jsonStr == null) continue;

      try {
        const event = JSON.parse(jsonStr);
        const applied = applySseEvent(state, event);

        if (applied.kind === 'update') {
          onUpdate(snapshotStreamState(state));
          continue;
        }

        if (applied.kind === 'error') {
          throwStreamError(applied.error, onError);
        }

        if (applied.kind === 'complete') {
          result = applied.result;
        }
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') throw e;
        if (e?.streamError) throw e;
        if (e instanceof SyntaxError) {
          Logger.warn('AI stream: ignored malformed SSE line', { line: jsonStr.slice(0, 120) });
        }
      }
    }
  }

  if (!result) {
    throwStreamError(streamIncompleteError(), onError);
  }

  return result;
}
